import UnfoldLessOutlinedIcon from '@mui/icons-material/UnfoldLessOutlined';
import UnfoldMoreOutlinedIcon from '@mui/icons-material/UnfoldMoreOutlined';
import { Alert, Box, Button, DialogContent, Stack, TextField, Typography } from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { runMultiplexPeakDetection } from 'api/platform';
import { useTrackActionFormDirty } from 'components/ActionDialog/actionFormDirtyState';
import { CollapsibleSection } from 'components/atoms/CollapsibleSection';
import { DialogChromeFooter } from 'components/atoms/Dialog/DialogChrome';
import { JobType, PeakDetectionJob } from 'interfaces/job';
import { MultiplexPanelsResponse, MultiplexPanelView } from 'interfaces/multiplexPanels';
import { every, filter, find, flatMap, includes, intersection, keys, map, pick, reduce, size, sortBy } from 'lodash';
import React, { useState } from 'react';
import { CasesParams } from 'utils/useCasesParams/types';
import { LoadFromPreviousJobStep } from '../../LoadFromPreviousJobStep';
import { PanelPeakDetectionEditor } from './PanelPeakDetectionEditor';
import { buildDefaultMogPeakDetectionChannelProfile, buildPanelPeakDetectionRequests } from './helpers';
import { MultiplexMogPeakDetectionChannelProfile, PanelPeakDetectionConfiguration } from './types';

interface Props {
  response: MultiplexPanelsResponse;
  casesParams: CasesParams;
  selectedSlideIds: string[];
  onClose: (event?: any, force?: boolean) => void;
  scopeMessage?: string;
  platformVersionWarning?: React.ReactNode;
}

const initialConfiguration = (panel: MultiplexPanelView, slideIds: string[]): PanelPeakDetectionConfiguration => {
  const slides = filter(panel.slides, (slide) => includes(slideIds, slide.slideId));
  const channelNames = map(
    filter(
      panel.channels,
      (channel) =>
        channel.saved &&
        size(slides) &&
        every(slides, (slide) => {
          const index = find(keys(slide.channelMapping), (key) => slide.channelMapping[key] === channel.channelIndex);
          return (
            index != null &&
            includes(slide.rnaChannelIds, index) &&
            slide.channelMarkerTypes[index] === channel.saved!.analysisName
          );
        })
    ),
    (channel) => channel.saved!.analysisName
  );
  return {
    channelNames,
    defaultProfile: buildDefaultMogPeakDetectionChannelProfile(),
    overrides: [],
  };
};

export const PanelSpotDetectionForm = ({
  response,
  casesParams,
  selectedSlideIds,
  onClose,
  scopeMessage,
  platformVersionWarning,
}: Props) => {
  const panels = sortBy(
    filter(
      response.panels,
      (panel) => panel.saved && size(intersection(map(panel.slides, 'slideId'), selectedSlideIds)) > 0
    ),
    (panel) => (panel.createdAt ? Date.parse(panel.createdAt) : Infinity)
  );
  const [configurations, setConfigurations] = useState<Record<string, PanelPeakDetectionConfiguration>>(() =>
    reduce(
      panels,
      (result, panel) => {
        result[panel.panelId] = initialConfiguration(panel, selectedSlideIds);
        return result;
      },
      {} as Record<string, PanelPeakDetectionConfiguration>
    )
  );
  const [selectedPanels, setSelectedPanels] = useState(() =>
    filter(map(panels, 'panelId'), (panelId) => size(configurations[panelId].channelNames) > 0)
  );
  const [jobName, setJobName] = useState('');
  const [collapsedPanelIds, setCollapsedPanelIds] = useState<string[]>([]);
  const anyPanelExpanded = panels.some((panel) => !collapsedPanelIds.includes(panel.panelId));
  const [jobDescription, setJobDescription] = useState('');
  const [resolution, setResolution] = useState('');
  const [previousJobId, setPreviousJobId] = useState('');
  const [isLoadingPreviousJob, setIsLoadingPreviousJob] = useState(false);
  const [restoreWarning, setRestoreWarning] = useState('');
  const [isDirty, setIsDirty] = useState(false);
  const [results, setResults] = useState<Record<string, 'started' | 'failed'>>({});
  useTrackActionFormDirty(isDirty);
  const mutation = useMutation(runMultiplexPeakDetection);
  let blockingMessage = restoreWarning;
  let requests: ReturnType<typeof buildPanelPeakDetectionRequests> = [];
  try {
    requests = buildPanelPeakDetectionRequests({
      panels,
      panelConfigurations: pick(
        configurations,
        filter(selectedPanels, (panelId) => results[panelId] !== 'started')
      ),
      selectedSlideIds,
      casesParams,
      jobName,
      jobDescription,
      resolutionUmpp: resolution === '' ? null : Number(resolution),
    });
    if (resolution !== '' && (!Number.isFinite(Number(resolution)) || Number(resolution) <= 0))
      blockingMessage = 'Custom resolution must be positive';
  } catch (error) {
    blockingMessage = error instanceof Error ? error.message : 'Review panel settings';
  }
  const unassignedCount = size(
    filter(selectedSlideIds, (slideId) => !find(panels, (panel) => find(panel.slides, { slideId })))
  );
  const submit = async () => {
    if (blockingMessage || isLoadingPreviousJob || mutation.isLoading) return;
    const nextResults = { ...results };
    for (const entry of requests) {
      try {
        await mutation.mutateAsync(entry.request);
        nextResults[entry.panelId] = 'started';
      } catch {
        nextResults[entry.panelId] = 'failed';
      }
      setResults({ ...nextResults });
    }
    if (size(requests) && every(selectedPanels, (panelId) => nextResults[panelId] === 'started'))
      onClose(undefined, true);
  };
  const loadPreviousJob = (job?: Partial<PeakDetectionJob>) => {
    setIsDirty(true);
    setPreviousJobId(job?.id || '');
    setJobName(job?.name || '');
    setJobDescription(job?.description || '');
    if (!job) {
      const reset = reduce(
        panels,
        (result, panel) => {
          result[panel.panelId] = initialConfiguration(panel, selectedSlideIds);
          return result;
        },
        {} as Record<string, PanelPeakDetectionConfiguration>
      );
      setConfigurations(reset);
      setSelectedPanels(filter(keys(reset), (panelId) => size(reset[panelId].channelNames) > 0));
      setRestoreWarning('');
      setResolution('');
      return;
    }
    const useRawMog = job.params?.useRawMog === true;
    const restored = reduce(
      panels,
      (result, panel) => {
        const available = map(panel.channels, (channel) => channel.saved?.analysisName || '');
        const channelNames = intersection(job.params?.channelNames || [], available);
        result[panel.panelId] = {
          ...initialConfiguration(panel, selectedSlideIds),
          channelNames,
          overrides: useRawMog
            ? map(channelNames, (channelName) => ({
                channelNames: [channelName],
                profile: {
                  ...buildDefaultMogPeakDetectionChannelProfile(),
                  ...(job.params?.profiles?.[channelName] as MultiplexMogPeakDetectionChannelProfile | undefined),
                },
              }))
            : [],
        };
        return result;
      },
      {} as Record<string, PanelPeakDetectionConfiguration>
    );
    const known = flatMap(restored, 'channelNames');
    const unknownCount = size(filter(job.params?.channelNames || [], (marker) => !includes(known, marker)));
    setRestoreWarning(unknownCount ? `${unknownCount} previous-job markers are unavailable in this panel scope` : '');
    setConfigurations(restored);
    setSelectedPanels(filter(keys(restored), (panelId) => size(restored[panelId].channelNames) > 0));
    setResolution(useRawMog && job.params?.resolutionUmpp != null ? String(job.params.resolutionUmpp) : '');
  };
  const pendingPanels = filter(
    panels,
    (panel) => includes(selectedPanels, panel.panelId) && results[panel.panelId] !== 'started'
  );
  const pendingSlideCount = new Set(
    flatMap(pendingPanels, (panel) =>
      map(
        filter(panel.slides, (slide) => includes(selectedSlideIds, slide.slideId)),
        'slideId'
      )
    )
  ).size;
  return (
    <>
      <DialogContent
        dividers
        sx={{ bgcolor: (theme) => (theme.palette.mode === 'light' ? 'common.white' : 'background.paper') }}
      >
        <Stack spacing={2}>
          {platformVersionWarning}
          {scopeMessage && <Alert severity="info">{scopeMessage}</Alert>}
          {!size(panels) ? (
            <Alert severity="info">Define multiplex panels for the selected slides before spot detection.</Alert>
          ) : null}
          {unassignedCount ? (
            <Alert severity="warning">{unassignedCount} selected slides have no saved panel and will not run.</Alert>
          ) : null}
          {!size(keys(results)) ? (
            <CollapsibleSection title="Reuse previous job settings">
              <LoadFromPreviousJobStep
                jobType={JobType.MultiplexPeakDetection}
                selectedJobId={previousJobId}
                onSelectedJob={(job) => loadPreviousJob(job as PeakDetectionJob)}
                onSelectedJobLoadingChange={setIsLoadingPreviousJob}
              />
            </CollapsibleSection>
          ) : null}
          <CollapsibleSection title="Job name and description">
            <Stack spacing={2} sx={{ mt: 1 }}>
              <TextField
                label="Job name"
                value={jobName}
                onChange={(event) => {
                  setIsDirty(true);
                  setJobName(event.target.value);
                }}
              />
              <TextField
                label="Job description"
                value={jobDescription}
                onChange={(event) => {
                  setIsDirty(true);
                  setJobDescription(event.target.value);
                }}
              />
            </Stack>
          </CollapsibleSection>
          {map(panels, (panel) => (
            <Stack key={panel.panelId} spacing={1}>
              <PanelPeakDetectionEditor
                panel={panel}
                expanded={!collapsedPanelIds.includes(panel.panelId)}
                onExpandedChange={(expanded) =>
                  setCollapsedPanelIds((current) =>
                    expanded ? current.filter((panelId) => panelId !== panel.panelId) : [...current, panel.panelId]
                  )
                }
                labId={casesParams.labId}
                customResolutionUmpp={resolution === '' ? null : Number(resolution)}
                included={includes(selectedPanels, panel.panelId)}
                selectionDisabled={mutation.isLoading || results[panel.panelId] === 'started'}
                onIncludeChange={(checked) => {
                  setIsDirty(true);
                  setSelectedPanels(
                    checked ? [...selectedPanels, panel.panelId] : filter(selectedPanels, (id) => id !== panel.panelId)
                  );
                }}
                configuration={configurations[panel.panelId] || initialConfiguration(panel, selectedSlideIds)}
                selectedSlideIds={selectedSlideIds}
                disabled={
                  mutation.isLoading || results[panel.panelId] === 'started' || !includes(selectedPanels, panel.panelId)
                }
                onChange={(configuration) => {
                  setIsDirty(true);
                  setConfigurations({ ...configurations, [panel.panelId]: configuration });
                }}
              />
              {results[panel.panelId] ? (
                <Alert severity={results[panel.panelId] === 'started' ? 'success' : 'error'}>
                  {panel.name}:{' '}
                  {results[panel.panelId] === 'started'
                    ? 'Spot detection started'
                    : 'Spot detection failed to start. Settings are retained for retry.'}
                </Alert>
              ) : null}
            </Stack>
          ))}
          <CollapsibleSection title="Advanced runtime settings">
            <Stack spacing={2} sx={{ mt: 1 }}>
              <TextField
                label="Custom resolution (UMPP)"
                type="number"
                value={resolution}
                helperText="Optional. Default: native slide resolution."
                onChange={(event) => {
                  setIsDirty(true);
                  setResolution(event.target.value);
                }}
              />
            </Stack>
          </CollapsibleSection>
          {restoreWarning ? (
            <Alert
              severity="warning"
              action={
                <Button color="inherit" onClick={() => setRestoreWarning('')}>
                  Discard unavailable marker settings
                </Button>
              }
            >
              {restoreWarning}
            </Alert>
          ) : null}
        </Stack>
      </DialogContent>
      <DialogChromeFooter
        sx={{
          flexShrink: 0,
          bgcolor: (theme) => (theme.palette.mode === 'light' ? 'common.white' : 'background.paper'),
        }}
      >
        <Box data-testid="spot-detection-footer" sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
          <Stack spacing={0.5} sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {size(keys(results))
                ? `Retry ${size(pendingPanels)} failed ${size(pendingPanels) === 1 ? 'panel' : 'panels'}`
                : `All ${size(pendingPanels)} included ${size(pendingPanels) === 1 ? 'panel' : 'panels'}`}{' '}
              · {pendingSlideCount} selected {pendingSlideCount === 1 ? 'slide' : 'slides'}
            </Typography>
            {size(keys(results)) > 0 && (
              <Typography variant="caption" color="text.secondary">
                Already-started panels will not run again.
              </Typography>
            )}
            {blockingMessage ? (
              <Typography role="status" variant="body2" color="text.secondary">
                {blockingMessage}
              </Typography>
            ) : null}
          </Stack>
          <Button
            color="inherit"
            variant="text"
            size="small"
            startIcon={anyPanelExpanded ? <UnfoldLessOutlinedIcon /> : <UnfoldMoreOutlinedIcon />}
            disabled={!panels.length}
            onClick={() => setCollapsedPanelIds(anyPanelExpanded ? panels.map((panel) => panel.panelId) : [])}
          >
            {anyPanelExpanded ? 'Collapse all panels' : 'Expand all panels'}
          </Button>
          <Button color="inherit" onClick={() => onClose()} disabled={mutation.isLoading}>
            Close
          </Button>
          <Button
            variant="contained"
            color="primary"
            disabled={Boolean(blockingMessage) || isLoadingPreviousJob || mutation.isLoading}
            onClick={() => void submit()}
          >
            {mutation.isLoading
              ? 'Starting spot detection'
              : size(keys(results))
              ? 'Retry failed panels'
              : 'Run spot detection'}
          </Button>
        </Box>
      </DialogChromeFooter>
    </>
  );
};
